import { FORMULA_TYPE, INDEX, VALUE_TYPE } from "../contanst";
import {
  ValueType,
  Value,
  Cell,
  CellValue,
  Field,
  Row,
  Loop,
  TableEditorConfig,
  ListValueType,
  Formula,
  FormulaType,
} from "../dto";
import { isNullOrUndefined } from "../../utils";
import { generateGrid } from "./transform";

// 是否为list值
export const isListValue = (
  value: Value
): value is {
  code: string;
  parentCode?: string;
} & ListValueType => value?.isList;

const evalJs = (str: string) => {
  try {
    return new Function(`return ${str}`)();
  } catch (e) {
    console.error(`【TableEditor】JS表达式错误：{${str}}`, e);
    return undefined;
  }
};

// 是否为序号code
export const isIndexCode = (() => {
  const regex = new RegExp(
    `^.+_${INDEX.code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`
  );
  return (code: string) => regex.test(code);
})();

// 千分位分隔
const fmtThousand = (num: string) => {
  const arr = num.split(".");
  return [arr[0].replace(/\B(?=(\d{3})+(?!\d))/g, ","), ...arr.slice(1)].join(
    "."
  );
};

// 格式化数字
const toFixed = (num: number, precision: number, truncate = false) => {
  if (
    Number.isNaN(num) ||
    [
      Infinity,
      -Infinity,
      Number.MAX_VALUE,
      Number.MIN_VALUE,
      -Number.MAX_VALUE,
      -Number.MIN_VALUE,
      Number.MAX_SAFE_INTEGER,
      Number.MIN_SAFE_INTEGER,
      -Number.MAX_SAFE_INTEGER,
      -Number.MIN_SAFE_INTEGER,
    ].includes(num)
  ) {
    return num;
  }
  let [integer, decimal = ""] = String(num).split(".");
  const integerPrecision = precision < 0 ? 0 : Math.round(precision);
  // 需要格式化小数位
  if (decimal.length >= integerPrecision + 1) {
    // 四舍五入OR截断
    const parse = truncate ? Math.floor : Math.round;
    // 如果0小数位
    if (integerPrecision === 0) {
      const d = parse(Number(`0.${decimal}`));
      // 进位
      integer = String(Number(integer) + (num > 0 ? d : -d));
      // 清空小数位
      decimal = "";
    } else {
      // 格式化小数位
      decimal = String(
        parse(
          Number(
            `${decimal.slice(0, integerPrecision)}.${decimal.slice(
              integerPrecision
            )}`
          )
        )
      );
      // 需要补前缀0
      if (decimal.length < integerPrecision) {
        decimal = decimal.padStart(integerPrecision, "0");
      }
      // 需要进位
      if (decimal.length > integerPrecision) {
        const d = Number(decimal.slice(0, decimal.length - integerPrecision));
        integer = String(Number(integer) + (num > 0 ? d : -d));
        decimal = decimal.slice(decimal.length - integerPrecision);
      }
    }
  }
  return Number(
    decimal.length > 0 && decimal !== "0" ? `${integer}.${decimal}` : integer
  );
};

// 四舍五入
export const round = (
  num: number,
  precision: number,
  trailingZeroes = false,
  thousandSeparator = false
) => {
  const val = toFixed(num, precision);
  let ret = String(val);
  if (trailingZeroes) {
    ret = val.toFixed(precision);
  }
  if (thousandSeparator) {
    ret = fmtThousand(ret);
  }
  return ret;
};

// 向下取整
export const truncate = (
  num: number,
  precision: number,
  trailingZeroes = false,
  thousandSeparator = false
) => {
  const val = toFixed(num, precision, true);
  let ret = String(val);
  if (trailingZeroes) {
    ret = val.toFixed(precision);
  }
  if (thousandSeparator) {
    ret = fmtThousand(ret);
  }
  return ret;
};

// 获取字段值
export const getFieldValue = (
  item: CellValue["value"][number],
  data: Value[],
  index?: number
) => {
  if (
    ([FORMULA_TYPE.TEXT, FORMULA_TYPE.OPERATOR] as string[]).includes(item.type)
  ) {
    return item.value;
  }
  let value = item.value as string | number;
  if (item.type === FORMULA_TYPE.DYNAMIC) {
    if (isIndexCode(item.value)) {
      return isNullOrUndefined(index) ? undefined : index + 1;
    }
    const d = data.find((v) => v.code === item.value);
    if (!d) {
      return undefined;
    }
    if (isListValue(d)) {
      if (isNullOrUndefined(index)) {
        value = d.valueList?.toString();
      } else {
        value = d.valueList?.[index]!;
      }
    } else {
      value = d.value!;
    }
  }
  return value;
};

/**
 * 获取单元格的所有字段取值路径
 * @param cell 单元格
 * @returns 单元格取值路径
 */
export const getFieldNamePath = (cell: Cell) => {
  const path: (string | number)[] =
    cell.value
      ?.filter((i) => i.type === FORMULA_TYPE.DYNAMIC)
      .map((i) => i.value) ?? [];
  return path;
};

export const flattenFields = (fields: Field[]): Omit<Field, "children">[] => {
  const ret = [];
  // eslint-disable-next-line no-underscore-dangle
  const _fittenFields = (_fields: Field[]) => {
    _fields.forEach((field) => {
      if (Array.isArray(field.children) && field.children.length > 0) {
        _fittenFields(field.children);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { children, ...rest } = field;
        ret.push(rest);
      }
    });
  };
  _fittenFields(fields);
  return ret;
};

export const getListBindsByValue = <
  T extends {
    code: string;
    isList?: boolean;
    parentCode?: string;
    type?: ValueType;
  }
>(
  value: CellValue["value"],
  fields: T[]
) => {
  const bindCodes: string[] = [];
  const parentCodes: string[] = [];
  const bindFields: { name: string; code: string; type: ValueType }[] = [];
  value?.forEach((i) => {
    const item = fields.find((f) => f.code === i.value);
    if (item?.isList) {
      bindCodes.push(item.code);
      bindFields.push({ name: i.label, code: item.code, type: item.type });
      if (item.parentCode) {
        parentCodes.push(item.parentCode);
      }
    }
  });
  const unqueBindCodes = Array.from(new Set(bindCodes));
  return {
    bindCodes: unqueBindCodes,
    parentCodes: Array.from(new Set(parentCodes)),
    bindFields: bindFields.filter((f) => unqueBindCodes.includes(f.code)),
  };
};

// 从cell获取绑定字段的code和父级code
export const getListBindsByCell = <
  T extends {
    code: string;
    isList?: boolean;
    parentCode?: string;
    type?: ValueType;
  }
>(
  cell: Cell,
  fields: T[]
) => getListBindsByValue(cell.value, fields);

// 从config获取绑定的字段的code和父级code
export const getListBindsByRowRange = <
  T extends {
    code: string;
    isList?: boolean;
    parentCode?: string;
    type?: ValueType;
  }
>(
  minRow: number,
  maxRow: number,
  config: TableEditorConfig,
  fields: T[]
) => {
  const bindCodes: string[] = [];
  const parentCodes: string[] = [];
  // 可能出现跨循环的单元个情况 所以需要通过grid来获取 而不是单纯通过rows获取
  const grid = generateGrid(config);
  const setIds = new Set();
  const cellsMap: Record<string, Cell> = {};
  for (const row of config.rows) {
    for (const cell of row.cells) {
      cellsMap[cell.id] = cell;
    }
  }
  const bindFields: { name: string; code: string; type: ValueType }[] = [];
  for (let i = minRow; i <= maxRow; i++) {
    const row = grid[i];
    for (let j = 0; j < row.length; j++) {
      const id = grid[i][j];
      if (setIds.has(id) || !cellsMap[id]) {
        continue;
      }
      const {
        bindCodes: rowBindCodes,
        parentCodes: rowParentCodes,
        bindFields: rowBindFields,
      } = getListBindsByCell(cellsMap[id], fields);
      bindCodes.push(...rowBindCodes);
      parentCodes.push(...rowParentCodes);
      bindFields.push(...rowBindFields);
    }
  }
  const unqueBindCodes = Array.from(new Set(bindCodes));
  return {
    bindCodes: unqueBindCodes,
    parentCodes: Array.from(new Set(parentCodes)),
    bindFields: unqueBindCodes
      .map((i) => bindFields.find((f) => f.code === i))
      .filter(Boolean),
  };
};

// 从loop获取绑定的字段的code和父级code
export const getListBindsByLoop = <
  T extends {
    code: string;
    isList?: boolean;
    parentCode?: string;
    type?: ValueType;
  }
>(
  loop: Loop,
  config: TableEditorConfig,
  fields: T[]
) => {
  const { minRow, maxRow } = loop;
  return getListBindsByRowRange(minRow, maxRow, config, fields);
};

// 是否为指定类型字段
const isFieldType = (
  item: CellValue["value"][0],
  type: ValueType,
  fields: Omit<Field, "children">[] | Value[]
) =>
  item.type === FORMULA_TYPE.DYNAMIC &&
  fields.some((f) => f.code === item.value && f.type === type);

/**
 * 根据DataType和value判断是否含有指定类型
 * @param type 需要判断的类型
 * @param dataType 数据类型
 * @param value 值
 * @returns 是否含有指定类型
 */
export const hasFieldType = (
  type: ValueType,
  value: CellValue["value"],
  fields: Omit<Field, "children">[] | Value[]
) => {
  const v = value ?? [];
  if (type === VALUE_TYPE.TEXT) {
    return v.some(
      (i) => i.type === FORMULA_TYPE.TEXT || isFieldType(i, type, fields)
    );
  }
  if (type === VALUE_TYPE.NUMBER) {
    return v.some(
      (i) => i.type === FORMULA_TYPE.NUMBER || isFieldType(i, type, fields)
    );
  }
  return v.some((i) => isFieldType(i, type, fields));
};

// 获取公式值
export const getFormulaValue = (value: Formula["value"], data: Value[]) => {
  if (value.length === 0) {
    return undefined;
  }
  // 是否为list
  let hasList = false;
  // 循环最大长度
  const len = Math.max(
    ...value.map((i) => {
      if (i.type !== FORMULA_TYPE.DYNAMIC) return 1;
      const d = data.find((v) => v.code === i.value);
      if (isListValue(d)) {
        hasList = true;
        return d.valueList.length;
      }
      return 1;
    })
  );
  // 替换字段为值生成公式数组
  const formula = Array.from({ length: len }).map((_, idx) => {
    return value
      .map((i) => {
        if (i.type !== FORMULA_TYPE.DYNAMIC) return i.value;
        const d = data.find((v) => v.code === i.value);
        return isListValue(d) ? d.valueList?.[idx] ?? 0 : d?.value ?? 0;
      })
      .join("");
  });
  // 计算结果值
  return hasList
    ? formula.map((i) => evalJs(i) ?? undefined)
    : evalJs(formula[0]) ?? undefined;
};

/**
 * 传入对象获取所有路径 如{a:2,b:[{c:2},{d:3}]}=>[["a"],["b","0","c"],["b","1","d"]]
 * @param obj 对象
 * @param prefix 路径前缀
 * @param result 结果集
 * @returns string[][]
 */
export const getKeyPaths = (obj = {}, prefix = [], result = []): string[][] => {
  // 遍历对象的每个属性
  for (const key in obj) {
    // 将当前属性添加到路径前缀中
    const newPrefix = [...prefix, key];
    // 如果当前属性是对象，则递归调用getKeyPaths
    if (typeof obj[key] === "object" && obj[key] !== null) {
      getKeyPaths(obj[key], newPrefix, result);
    } else {
      // 如果当前属性不是对象，则将路径添加到结果中
      result.push(newPrefix);
    }
  }
  // 返回所有路径
  return result;
};
// 获取公式中list字段
export const getFormulaListField = (
  formulaValue: Formula["value"],
  fields: Omit<Field, "children">[]
) =>
  fields.find((j) => formulaValue.find((k) => k.value === j.code && j.isList));
// 是否含有list字段
export const isFormulaHasListField = (
  formulaValue: Formula["value"],
  fields: Omit<Field, "children">[]
) =>
  fields.some((j) => formulaValue.some((k) => k.value === j.code && j.isList));
// 是否为字段
export const isField = (value: string, type: FormulaType) =>
  type === FORMULA_TYPE.DYNAMIC && !isIndexCode(value);
// 是否为有效公式
export const isVaildFormila = (formula: Formula, fieldCodes: string[]) =>
  formula.value.length > 0 &&
  formula.value.every(
    (i) => !isField(i.value, i.type) || fieldCodes.includes(i.value)
  );

// 获取字段路径
export const getFieldPath = (
  value: Cell["value"],
  dataSource: Value[],
  index?: number
) => {
  const name = value?.find((i) => isField(i.value, i.type))?.value;
  const field = dataSource?.find((i) => i.code === name);
  if (!name) return undefined;
  const path =
    !isNullOrUndefined(index) && field?.isList ? [name, index] : name;
  return path;
};
